import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useT, withT } from '~app/core/BundleProvider';

import useGlobalHotkey from '~app/core/useGlobalHotkey';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as DocumentActions } from '~app/features/document';

import CanvasNode from './CanvasNode';
import FlowArrow from './FlowArrow';
import InfinityNode from './InfinityNode';
import SignalArrow from './SignalArrow';
import SignalHandle from './SignalHandle';
import LabelBank from '~app/features/canvas/components/LabelBank';
import InstructionsBox from '~app/features/canvas/components/InstructionsBox';
import StepThroughPanel from '~app/features/canvas/components/StepThroughPanel';
import ErrorPanel from '~app/features/canvas/components/ErrorPanel';
import { CANVAS_SIZE } from '../consts';

import AriaModal from 'react-aria-modal';
import ContinueDialog from '~app/features/app/components/ContinueDialog';

import KeyboardShortcuts from '~app/features/canvas/components/KeyboardShortcuts';

const SHOW_ANCHOR_LINES = false; // for debugging, set true to visualize anchor data

const Canvas = () => {
  const t = useT();
  const dispatch = useDispatch();
  const labels = useSelector(state => state.document.present?.labels);
  const hasDocument = useSelector(
    state => !!state.document.present?.workingModel,
  );
  const nodes =
    useSelector(state => state.document.present?.workingModel?.nodes) ?? {};
  const signals =
    useSelector(state => state.document.present?.workingModel?.signals) ?? [];
  const labelBankExpanded = useSelector(
    state => state.canvas.labelBankExpanded,
  );
  const showKeyboardShortcuts = useSelector(
    state => state.canvas.showKeyboardShortcuts,
  );
  const selection = useSelector(state => state.canvas.selection);
  const viewportSize = useSelector(state => state.canvas.viewportSize);
  const scale = useSelector(state => state.canvas.scale);
  const offset = useSelector(state => state.canvas.offset);
  const instructionsBoxPosition = useSelector(
    state => state.canvas.instructionsBox.position,
  );
  const keyboardShortcutsPosition = useSelector(
    state => state.canvas.keyboardShortcutsPosition,
  );
  const instructions = useSelector(
    state => state.document.present?.instructions,
  );
  const instructionHeader = useSelector(
    state => state.document.present?.instructionHeader,
  );
  const name = useSelector(state => state.document.present?.name);
  const editMode = useSelector(state => state.document.present?.editMode);
  const arrowAnchors =
    useSelector(state => state.document.present?.arrowAnchors) ?? {};
  const isInCompareMode = useSelector(
    state => state.canvas?.compareWithSolution ?? false,
  );
  const labelFocus = useSelector(state => state.canvas.labelFocus);

  const duplicateName = useSelector(
    state => state.document.present?.duplicateName,
  );
  const resetDuplicateName = useCallback(
    () => dispatch(DocumentActions.resetDuplicateName()),
    [dispatch],
  );

  const viewportRef = useRef();
  const history = useHistory();

  // Move the instructionsBox if necessary to keep it visible when the viewport is resized
  useEffect(() => {
    if (viewportSize.x > 0 && viewportSize.y > 0) {
      let newx = instructionsBoxPosition.x;
      let newy = instructionsBoxPosition.y;
      if (instructionsBoxPosition.x < 0) {
        newx = 0;
      } else if (instructionsBoxPosition.x > viewportSize.x - 100) {
        newx = viewportSize.x - 100;
      }
      if (instructionsBoxPosition.y < 0) {
        newy = 0;
      } else if (instructionsBoxPosition.y > viewportSize.y - 50) {
        newy = viewportSize.y - 50;
      }
      if (
        newx !== instructionsBoxPosition.x ||
        newy !== instructionsBoxPosition.y
      ) {
        dispatch(CanvasActions.moveInstructionsBox({ x: newx, y: newy }));
      }
    }
  }, [instructionsBoxPosition, viewportSize, dispatch]);

  // Move the keyboardShortcuts if necessary to keep it visible when the viewport is resized
  useEffect(() => {
    if (viewportSize.x > 0 && viewportSize.y > 0) {
      let newx = keyboardShortcutsPosition.x;
      let newy = keyboardShortcutsPosition.y;
      if (keyboardShortcutsPosition.x < 0) {
        newx = 0;
      } else if (keyboardShortcutsPosition.x > viewportSize.x - 100) {
        newx = viewportSize.x - 100;
      }
      if (keyboardShortcutsPosition.y < 0) {
        newy = 0;
      } else if (keyboardShortcutsPosition.y > viewportSize.y - 50) {
        newy = viewportSize.y - 50;
      }
      if (
        newx !== keyboardShortcutsPosition.x ||
        newy !== keyboardShortcutsPosition.y
      ) {
        dispatch(CanvasActions.moveKeyboardShortcuts({ x: newx, y: newy }));
      }
    }
  }, [keyboardShortcutsPosition, viewportSize, dispatch]);

  useEffect(() => {
    if (!hasDocument) history.push('/');
  }, [hasDocument, history]);

  const maxInstructionsHeight = viewportSize.y - instructionsBoxPosition.y;

  // sets x, y position using new x and y position of the mouse drag
  const dragMap = useMemo(
    () => ({
      flowHead: data =>
        DocumentActions.moveFlowArrow({
          label: data.node.dataset.label,
          arrow: 'head',
          x: data.x,
          y: data.y,
        }),
      flowTail: data =>
        DocumentActions.moveFlowArrow({
          label: data.node.dataset.label,
          arrow: 'tail',
          x: data.x,
          y: data.y,
        }),
      node: data =>
        DocumentActions.moveNode({
          label: data.node.dataset.label,
          x: data.x + (arrowAnchors[data.node.dataset.label]?.center?.x ?? 0),
          y: data.y + (arrowAnchors[data.node.dataset.label]?.center?.y ?? 0),
        }),
      signal: data =>
        DocumentActions.moveSignal({
          fromLabel: data.node.dataset.from,
          toLabel: data.node.dataset.to,
          x: data.x,
          y: data.y,
        }),
      instructionsBox: data =>
        CanvasActions.moveInstructionsBox({ x: data.x, y: data.y }),
      keyboardShortcuts: data =>
        CanvasActions.moveKeyboardShortcuts({ x: data.x, y: data.y }),
    }),
    [arrowAnchors],
  );

  // sets new x and y using direction and value
  const moveMap = useMemo(
    () => ({
      flowHead: data =>
        DocumentActions.moveFlowArrow({
          label: data.label,
          arrow: 'head',
          direction: data.direction,
          value: data.value,
        }),
      flowTail: data =>
        DocumentActions.moveFlowArrow({
          label: data.label,
          arrow: 'tail',
          direction: data.direction,
          value: data.value,
        }),
      node: data =>
        DocumentActions.moveNode({
          label: data.label,
          direction: data.direction,
          value: data.value,
        }),
      signal: data =>
        DocumentActions.moveSignal({
          fromLabel: data.from,
          toLabel: data.to,
          direction: data.direction,
          value: data.value,
        }),
      instructionsBox: data =>
        CanvasActions.moveInstructionsBox({
          direction: data.direction,
          value: data.value,
        }),
      keyboardShortcuts: data =>
        CanvasActions.moveKeyboardShortcuts({
          direction: data.direction,
          value: data.value,
        }),
    }),
    [],
  );

  const deselect = useCallback(() => {
    hasDocument && dispatch(CanvasActions.selectItem({}));
  }, [dispatch, hasDocument]);

  const [panning, setPanning] = useState({
    isDown: false,
    lastClientX: 0,
    lastClientY: 0,
    moved: 0,
  });

  const onMouseDown = useCallback(
    e => {
      if (e.target === e.currentTarget) {
        e.stopPropagation();
        setPanning({
          isDown: true,
          lastClientX: e.clientX,
          lastClientY: e.clientY,
          moved: 0,
        });
      }
    },
    [setPanning],
  );

  const onMouseUp = useCallback(
    e => {
      if (panning.isDown) {
        if (panning.moved < 2) {
          deselect();
        }
        setPanning({ isDown: false });
      }
    },
    [setPanning, deselect, panning],
  );

  const onMouseMove = useCallback(
    e => {
      if (panning.isDown) {
        viewportRef.current.scrollLeft -= -panning.lastClientX + e.clientX;
        viewportRef.current.scrollTop -= -panning.lastClientY + e.clientY;
        setPanning({
          isDown: true,
          lastClientX: e.clientX,
          lastClientY: e.clientY,
          moved: panning.moved + 1,
        });
      }
    },
    [panning, setPanning],
  );

  useEffect(() => {
    if (viewportRef.current && !panning.isDown) {
      viewportRef.current.scrollLeft = offset.x;
      viewportRef.current.scrollTop = offset.y;
    }
  }, [viewportRef, offset, panning]);

  // onDrag to move the items on canvas using mouse drag
  const onDrag = useCallback(
    (e, data) => {
      const type = data.node.dataset.type;
      dispatch(dragMap[type](data));
    },
    [dragMap, dispatch],
  );

  const selectItem = useCallback(
    ({ type, label, from, to }) => {
      if (hasDocument) {
        const obj = { type, label, from, to };
        if (type === 'signal') {
          obj.label = from + '>' + to;
        }
        dispatch(CanvasActions.selectItem(obj));
      }
    },
    [dispatch, hasDocument],
  );

  // moveItem to move the items on canvas using keyboard
  const moveItem = useCallback(
    direction => {
      if (selection.type) {
        const type = selection.type;
        const data = {
          label: selection.label,
          from: selection.from,
          to: selection.to,
          direction: direction, // left,right,up,down directions
          value: 10, // moving 10 steps in the direction
        };
        dispatch(moveMap[type](data));
      }
    },
    [moveMap, dispatch, selection],
  );

  const onClick = useCallback(
    e => {
      e.stopPropagation();
      selectItem(e.currentTarget.dataset);
    },
    [selectItem],
  );

  const onTouchStart = useCallback(
    e => {
      e.stopPropagation();
      selectItem(e.currentTarget.dataset);
    },
    [selectItem],
  );

  const onKeyPress = useCallback(
    e => {
      if (labelFocus !== true && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        selectItem(e.currentTarget.dataset);
      }
    },
    [selectItem, labelFocus],
  );

  const onScroll = useCallback(
    () =>
      dispatch(
        CanvasActions.updateCanvasScroll({
          x: viewportRef.current.scrollLeft,
          y: viewportRef.current.scrollTop,
        }),
      ),
    [dispatch, viewportRef],
  );

  const onResize = useCallback(
    () =>
      dispatch(
        CanvasActions.updateViewportSize({
          x: viewportRef.current.offsetWidth,
          y: viewportRef.current.offsetHeight,
        }),
      ),
    [dispatch, viewportRef],
  );

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  const onAnchorDataChange = useCallback(
    (element, data) => {
      const label = element.dataset.label;
      if (label) {
        dispatch(DocumentActions.updateAnchorData({ label, data }));
      }
    },
    [dispatch],
  );

  const zoomToFit = useCallback(() => dispatch(CanvasActions.zoomToFit()), [
    dispatch,
  ]);

  useGlobalHotkey({ hotkey: 'Escape', callback: deselect, event: 'keyup' });
  useGlobalHotkey({ hotkey: 'mod+a', callback: zoomToFit, event: 'keydown' });
  useGlobalHotkey({
    hotkey: 'up',
    callback: () => {
      moveItem('up');
    },
    event: 'keydown',
  });
  useGlobalHotkey({
    hotkey: 'left',
    callback: () => {
      moveItem('left');
    },
    event: 'keydown',
  });
  useGlobalHotkey({
    hotkey: 'right',
    callback: () => {
      moveItem('right');
    },
    event: 'keydown',
  });
  useGlobalHotkey({
    hotkey: 'down',
    callback: () => {
      moveItem('down');
    },
    event: 'keydown',
  });

  const dprops = { onDrag, scale, bounds: 'parent' };

  return (
    <div className="canvas__container">
      {labelBankExpanded && <LabelBank viewportRef={viewportRef} />}
      <StepThroughPanel />
      {isInCompareMode && <ErrorPanel />}

      <div className="canvas__viewport" ref={viewportRef} onScroll={onScroll}>
        <div
          className="canvas__zoom"
          style={{ width: CANVAS_SIZE * scale, height: CANVAS_SIZE * scale }}
        >
          <div
            id="capture"
            className="canvas__surface"
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            style={{
              transformOrigin: 'top left',
              transform: `scale(${scale})`,
              width: CANVAS_SIZE,
              height: CANVAS_SIZE,
            }}
          >
            {/* arrows */}
            <svg className="canvas__SVG">
              {Object.keys(nodes)
                .filter(label => nodes[label].type === 'flow')
                .map(label => (
                  <FlowArrow key={`${label}-arrow`} flow={label} />
                ))}
            </svg>

            {/* nodes */}
            {Object.keys(nodes).map(label => (
              <CanvasNode
                key={label}
                label={labels[label]}
                type={nodes[label].type}
                draggableProps={{
                  position: {
                    x:
                      nodes[label].position.x -
                      (arrowAnchors[label]?.center?.x ?? 0),
                    y:
                      nodes[label].position.y -
                      (arrowAnchors[label]?.center?.y ?? 0),
                  },
                  ...dprops,
                }}
                onLabelChange={
                  editMode === 'solution'
                    ? text =>
                        dispatch(
                          DocumentActions.updateLabelText({ label, text }),
                        )
                    : undefined
                }
                divProps={{
                  'data-type': 'node',
                  'data-label': label,
                  onClick,
                  onKeyPress,
                  onTouchStart,
                }}
                selected={
                  selection.label === label && selection.type === 'node'
                }
                onAnchorDataChange={onAnchorDataChange}
                hasError={(isInCompareMode && nodes[label]?.hasError) || null}
                hasSourceError={
                  (isInCompareMode && nodes[label]?.hasSourceError) || null
                }
                hasSinkError={
                  (isInCompareMode && nodes[label]?.hasSinkError) || null
                }
                pendingRecheck={nodes[label]?.pendingRecheck}
                labelFocus={labelFocus}
              />
            ))}

            {/* unconnected flow ends */}
            {Object.keys(nodes)
              .filter(
                label =>
                  nodes[label].type === 'flow' &&
                  nodes[label].arrowHeadPosition,
              )
              .map(label => (
                <InfinityNode
                  key={`${label}+head`}
                  draggableProps={{
                    position: nodes[label].arrowHeadPosition,
                    ...dprops,
                  }}
                  divProps={{
                    'data-type': 'flowHead',
                    'data-label': label,
                    onClick,
                    onKeyPress,
                    onTouchStart,
                  }}
                  type="flowHead"
                  selected={
                    selection.label === label && selection.type === 'flowHead'
                  }
                />
              ))}
            {Object.keys(nodes)
              .filter(
                label =>
                  nodes[label].type === 'flow' &&
                  nodes[label].arrowTailPosition,
              )
              .map(label => (
                <InfinityNode
                  key={`${label}+tail`}
                  draggableProps={{
                    position: nodes[label].arrowTailPosition,
                    ...dprops,
                  }}
                  divProps={{
                    'data-type': 'flowTail',
                    'data-label': label,
                    onClick,
                    onKeyPress,
                    onTouchStart,
                  }}
                  type="flowTail"
                  selected={
                    selection.label === label && selection.type === 'flowTail'
                  }
                />
              ))}

            {/* signals */}
            <svg className="canvas__SVG">
              {signals.map(s => (
                <SignalArrow
                  key={`${s.connectFrom} -> ${s.connectTo} arrow`}
                  signal={s}
                />
              ))}
            </svg>
            {signals.map(s => (
              <SignalHandle
                key={`${s.connectFrom} -> ${s.connectTo}`}
                polarity={s.polarity}
                draggableProps={{ position: s.position, ...dprops }}
                divProps={{
                  'data-type': 'signal',
                  'data-from': s.connectFrom,
                  'data-to': s.connectTo,
                  onClick,
                  onKeyPress,
                  onTouchStart,
                }}
                selected={
                  selection.label === `${s.connectFrom}>${s.connectTo}` &&
                  selection.type === 'signal'
                }
                hasError={isInCompareMode && s.hasError}
                pendingRecheck={s?.pendingRecheck}
              />
            ))}

            {SHOW_ANCHOR_LINES && (
              <svg className="canvas__SVG">
                {Object.keys(arrowAnchors).map(label => {
                  const data = arrowAnchors[label];
                  const node = nodes[label];
                  return (
                    <g key={label}>
                      <circle
                        className="dbg_node_center"
                        cx={node.position.x}
                        cy={node.position.y}
                        r={3}
                      />
                      {data.lines.map(({ x1, y1, x2, y2 }) => (
                        <line
                          key={`${x1},${y1},${x2},${y2}`}
                          className="dbg_node_anchor_line"
                          x1={x1 + node.position.x}
                          y1={y1 + node.position.y}
                          x2={x2 + node.position.x}
                          y2={y2 + node.position.y}
                        />
                      ))}
                      {data.flowPoints.map(({ x, y }) => (
                        <circle
                          key={`${x},${y}`}
                          className="dbg_node_flow_point"
                          cx={x + node.position.x}
                          cy={y + node.position.y}
                          r={3}
                        />
                      ))}
                    </g>
                  );
                })}
              </svg>
            )}
          </div>
        </div>
      </div>
      {/* instructions */}
      {instructions && name && (
        <InstructionsBox
          draggableProps={{ position: instructionsBoxPosition, onDrag }}
          divProps={{
            'data-type': 'instructionsBox',
            onClick,
            onKeyPress,
            onTouchStart,
          }}
          title={instructionHeader ?? name}
          description={instructions}
          maxHeight={maxInstructionsHeight}
          selected={selection.type === 'instructionsBox'}
          labelFocus={labelFocus}
        />
      )}
      {showKeyboardShortcuts && (
        <KeyboardShortcuts
          draggableProps={{ position: keyboardShortcutsPosition, onDrag }}
          divProps={{
            'data-type': 'keyboardShortcuts',
            onClick,
            onKeyPress,
            onTouchStart,
          }}
          selected={selection.type === 'keyboardShortcuts'}
        />
      )}
      <AriaModal mounted={duplicateName} titleText={t('confirm-exit-title')}>
        <ContinueDialog
          title={t('duplicate-object-name')}
          text={t('duplicate-object-warning')}
          onContinue={() => resetDuplicateName()}
          continueLabel={t('continue-button')}
        />
      </AriaModal>
    </div>
  );
};

export default withT(Canvas);
